import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { Flex, Heading, Icon } from '@qga/roo-ui/components';
import { themeGet } from 'styled-system';

import VisuallyHidden from '@shared/v2/components/VisuallyHidden';
import { ReturnFlightJourney } from '@shared/v2/services/types/api/parsedResponse';

import type { FlightCTAProps } from '../../../../../components/FlightCTA';
import FlightCTA from '../../../../../components/FlightCTA';
import FlightsSegment from '../../../../../components/FlightsSegment';
import FlightsSegmentBox from '../../../../../components/FlightsSegmentBox';

type ReturnFlightOption = ReturnFlightJourney;

const Section = styled('section')`
  & + & {
    border-top: 1px solid ${themeGet('dividers.horizontal.backgroundColor')};
    padding-top: ${themeGet('space.4')};
  }
`;

type FlightListOptionSectionProps = {
  children: ReactNode;
  title: 'Departing' | 'Returning';
};

const FlightListOptionSection = ({
  children,
  title,
}: FlightListOptionSectionProps) => (
  <Section>
    <Heading.h4 textStyle="text" mb="4">
      <Icon
        name={title === 'Returning' ? 'flightLand' : 'flightTakeoff'}
        size={20}
        aria-hidden="true"
        mr="2"
      />
      {title}
    </Heading.h4>
    {children}
  </Section>
);

export type ReturnFlightListOptionProps = {
  flightOption: ReturnFlightOption;
  isSelected?: boolean;
  charges: FlightCTAProps['charges'];
  onSelect: FlightCTAProps['onSelected'];
};

export const ReturnFlightListOption = ({
  flightOption,
  isSelected,
  charges,
  onSelect,
}: ReturnFlightListOptionProps): JSX.Element => {
  const { flightSegments } = flightOption;
  return (
    <FlightsSegmentBox
      data-testid="RETURN_FLIGHT_OPTION"
      isSelected={isSelected}
    >
      <VisuallyHidden>
        <h3>
          Departing {flightSegments.outbound[0].flightNumber} & Returning{' '}
          {flightSegments.inbound[0].flightNumber}
        </h3>
      </VisuallyHidden>
      <Flex flexDirection="column" gap={4}>
        <FlightListOptionSection title="Departing">
          <FlightsSegment flights={flightSegments.outbound} />
        </FlightListOptionSection>
        <FlightListOptionSection title="Returning">
          <FlightsSegment flights={flightSegments.inbound} />
        </FlightListOptionSection>
      </Flex>
      <FlightCTA
        isSelected={isSelected}
        charges={charges}
        onSelected={onSelect}
      />
    </FlightsSegmentBox>
  );
};
